import { Entity } from "./Entity.js";
import {
  MAX_HP, MAX_JUMPS, MOVE_SPEED, ACCEL, FRICTION,
  JUMP_FORCE, DOUBLE_JUMP_FORCE, DOUBLE_JUMP_HBOOST,
  CLIMB_SPEED, DASH_SPEED, SLIDE_FRICTION,
  GRAVITY, FALL_GRAVITY, APEX_GRAVITY, APEX_VY_THRESHOLD, MAX_FALL_SPEED,
} from "../constants.js";
import { WEAPONS, DEFAULT_MELEE_WEAPON, DEFAULT_THROW_WEAPON } from "../data/weapons.js";

function scaleDamping(basePerFrame, dtScale) {
  return Math.pow(basePerFrame, dtScale);
}

export class Player extends Entity {
  constructor(scene, spawnX, spawnY) {
    super(scene, spawnX, spawnY - 40, 28, 40, MAX_HP);
    this.setHurtboxProfile("player");
    this.spawnX = spawnX;
    this.spawnY = spawnY;
    this.onGround = false;
    this.jumpsLeft = MAX_JUMPS;
    this.droppedShape = null;
    this.onLadder = false;
    this.ducking = false;
    this.sliding = false;
    this.attacking = false;
    this.attackType = "";
    this.attackTime = 0;
    this.attackHitDealt = false;
    this.projectileSpawned = false;
    this.effectSpawned = false;
    this.throwFacing = null;
    this.equippedMelee = DEFAULT_MELEE_WEAPON;
    this.equippedThrowable = DEFAULT_THROW_WEAPON;
  }

  getWeaponDef(attackType) {
    if (attackType === "throw") return WEAPONS[this.equippedThrowable];
    return WEAPONS[this.equippedMelee];
  }

  enterLowProfile() {
    if (this.h === 26) return;
    this.y += 14;
    this.h = 26;
  }

  exitLowProfile() {
    if (this.h === 40) return;
    this.y -= 14;
    this.h = 40;
  }

  processInput(keys, prevKeys, dt, dtScale = 1) {
    const scene = this.scene;

    // Hit-stop: freeze all input and attack timer
    if (this.hitStopTimer > 0) return;

    // Attack timer
    if (this.attacking) {
      this.attackTime += dt;
      const weaponDef = this.getWeaponDef(this.attackType);
      const attack = weaponDef?.attacks?.[this.attackType];
      const dur = attack ? attack.duration : 400;
      if (this.attackTime >= dur) {
        if (this.attackType === "throw") this.throwFacing = null;
        this.attacking = false;
        this.attackType = "";
        this.attackHitDealt = false;
      }
    }

    if (this.attacking && this.attackType === "throw" && this.throwFacing != null) {
      this.facing = this.throwFacing;
    }

    // Ladder grab
    const overlapsLadder = scene.isOnLadder(this);
    if (this.onLadder) {
      this.onLadder = overlapsLadder;
    } else if (overlapsLadder && (keys.up || keys.down) && !(this.onGround && keys.up && !scene.isOnLadder({ x: this.x, y: this.y - 4, w: this.w, h: this.h }))) {
      this.onLadder = true;
      const ladderCenterX = scene.getLadderCenterX(this);
      if (ladderCenterX >= 0) this.x = ladderCenterX - this.w / 2;
      this.vx = 0;
    }

    // Horizontal movement
    if (this.sliding) {
      // locked
    } else if (this.attacking && this.onGround && this.attackType !== "throw") {
      this.vx = 0;
    } else if (this.onLadder) {
      this.vx = 0;
    } else if (this.onGround) {
      const accel = Math.min(1, ACCEL * dtScale);
      if (keys.left) { this.vx += (-MOVE_SPEED - this.vx) * accel; if (!this.attacking) this.facing = -1; }
      else if (keys.right) { this.vx += (MOVE_SPEED - this.vx) * accel; if (!this.attacking) this.facing = 1; }
      else { this.vx *= scaleDamping(FRICTION, dtScale); if (Math.abs(this.vx) < 0.1) this.vx = 0; }
    } else {
      if (keys.left) { if (!this.attacking) this.facing = -1; }
      else if (keys.right) { if (!this.attacking) this.facing = 1; }
    }

    // Jump (edge-triggered)
    if (keys.space && !prevKeys.jump) {
      let dropped = false;
      if (keys.down && this.onGround) {
        const feetY = this.y + this.h;
        const leftX = this.x + 2;
        const rightX = this.x + this.w - 2;
        const left = scene.findGroundY(leftX, feetY - 8, feetY + 8, null);
        const right = scene.findGroundY(rightX, feetY - 8, feetY + 8, null);
        let ground = null;
        if (left && right) ground = left.y < right.y ? left : right;
        else ground = left || right;
        if (ground && ground.shape.oneWay) {
          this.droppedShape = ground.shape;
          this.vy = -1.5;
          this.onGround = false;
          dropped = true;
        }
      }
      if (!dropped && this.jumpsLeft > 0) {
        if (this.onLadder) {
          this.vy = JUMP_FORCE * 0.3;
          if (keys.left) { this.vx = -MOVE_SPEED; this.facing = -1; }
          else if (keys.right) { this.vx = MOVE_SPEED; this.facing = 1; }
          this.onLadder = false;
        } else if (this.jumpsLeft === MAX_JUMPS) {
          this.vy = JUMP_FORCE;
        } else {
          if (keys.up) {
            // Up held — jump straight up
            this.vy = DOUBLE_JUMP_FORCE * 2.1;
          } else if (Math.abs(this.vx) > 0.5) {
            // Already moving — jump forward in current direction
            this.vy = DOUBLE_JUMP_FORCE;
            this.vx = this.facing * (MOVE_SPEED + DOUBLE_JUMP_HBOOST);
          } else {
            // Standing still — jump forward in facing direction
            this.vy = DOUBLE_JUMP_FORCE;
            this.vx = this.facing * (MOVE_SPEED + DOUBLE_JUMP_HBOOST);
          }
        }
        this.jumpsLeft--;
        this.onGround = false;
      }
    }
    prevKeys.jump = keys.space;

    // Duck (down arrow): use slide stance without dash movement.
    const canDuck = this.onGround && !this.onLadder && !this.sliding;
    if (canDuck && keys.down) {
      this.ducking = true;
      this.enterLowProfile();
    } else if (this.ducking) {
      this.ducking = false;
      this.exitLowProfile();
    }

    // Slide-dash
    if (keys.f && !prevKeys.dash && this.onGround && !this.onLadder && !this.sliding && !this.attacking) {
      this.sliding = true;
      this.ducking = false;
      this.vx = this.facing * DASH_SPEED;
      this.enterLowProfile();
    }
    prevKeys.dash = keys.f;

    if (this.sliding) {
      this.vx *= scaleDamping(SLIDE_FRICTION, dtScale);
      if (Math.abs(this.vx) < 1.2 || !this.onGround) {
        this.sliding = false;
        if (this.onGround && keys.down && !this.onLadder) {
          this.ducking = true;
          this.enterLowProfile();
        } else {
          this.ducking = false;
          this.exitLowProfile();
        }
      }
    }

    // Attacks (edge-triggered)
    const canAttack = !this.attacking && !this.sliding && !this.onLadder && !this.hurting;
    if (keys.d && !prevKeys.slash && canAttack) { this.attacking = true; this.attackType = "slash"; this.attackTime = 0; this.attackHitDealt = false; this.effectSpawned = false; }
    prevKeys.slash = keys.d;
    if (keys.a && !prevKeys.throw && canAttack) { this.attacking = true; this.attackType = "throw"; this.attackTime = 0; this.attackHitDealt = false; this.projectileSpawned = false; this.effectSpawned = false; }
    if (keys.a && !prevKeys.throw && canAttack) this.throwFacing = this.facing;
    prevKeys.throw = keys.a;
    if (keys.s && !prevKeys.kick && canAttack && this.onGround) { this.attacking = true; this.attackType = "kick"; this.attackTime = 0; this.attackHitDealt = false; this.effectSpawned = false; }
    prevKeys.kick = keys.s;

    // Ladder climbing
    if (this.onLadder) {
      if (keys.up) this.vy = -CLIMB_SPEED;
      else if (keys.down) this.vy = CLIMB_SPEED;
      else this.vy = 0;
      this.jumpsLeft = MAX_JUMPS;
    }
  }

  applyGravity(dtScale = 1) {
    if (this.onLadder) {
      // no gravity
    } else if (!this.onGround) {
      if (this.vy <= 0) {
        this.vy += (Math.abs(this.vy) < APEX_VY_THRESHOLD ? APEX_GRAVITY : GRAVITY) * dtScale;
      } else {
        const fallBlend = Math.max(0, Math.min(1, this.vy / Math.max(1, MAX_FALL_SPEED)));
        const blendedFallGravity = APEX_GRAVITY + (FALL_GRAVITY - APEX_GRAVITY) * fallBlend;
        this.vy += blendedFallGravity * dtScale;
      }
      this.vy = Math.min(this.vy, MAX_FALL_SPEED);
    } else {
      this.vy += GRAVITY * dtScale;
    }
  }

  getActiveHitbox() {
    if (!this.attacking) return null;
    const weaponDef = this.getWeaponDef(this.attackType);
    const attack = weaponDef?.attacks?.[this.attackType];
    if (!attack || !attack.hitbox) return null;

    const progress = this.attackTime / attack.duration;
    if (progress < attack.activeWindow.start || progress > attack.activeWindow.end) return null;

    const cx = this.x + this.w / 2;
    const cy = this.y + this.h / 2;
    return {
      x: cx + this.facing * attack.hitbox.offsetX - attack.hitbox.w / 2,
      y: cy + attack.hitbox.offsetY - attack.hitbox.h / 2,
      w: attack.hitbox.w,
      h: attack.hitbox.h,
      damage: attack.damage,
    };
  }

  getBounds() {
    const b = super.getBounds();
    if (!this.sliding) return b;

    // Sliding acts like a dodge: reduce hurtbox height from the top while
    // keeping feet grounded (bottom edge unchanged).
    const reduceTop = 40;
    return {
      x: b.x,
      y: b.y + reduceTop,
      w: b.w,
      h: Math.max(18, b.h - reduceTop),
    };
  }

  heal(amount) {
    if (this.dead || this.dying || amount <= 0) return;
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }

  reset() {
    this.x = this.spawnX;
    this.y = this.spawnY - 40;
    this.w = 28;
    this.h = 40;
    this.vx = 0;
    this.vy = 0;
    this.onGround = false;
    this.facing = 1;
    this.jumpsLeft = MAX_JUMPS;
    this.droppedShape = null;
    this.onLadder = false;
    this.ducking = false;
    this.sliding = false;
    this.hp = this.maxHp;
    this.attacking = false;
    this.attackType = "";
    this.attackTime = 0;
    this.attackHitDealt = false;
    this.projectileSpawned = false;
    this.effectSpawned = false;
    this.throwFacing = null;
    this.hurting = false;
    this.hurtTime = 0;
    this.hurtGraceTimer = 0;
    this.lastCombatMode = "pve";
    this.dying = false;
    this.dieTime = 0;
    this.dead = false;
    this.hitStopTimer = 0;
    this.knockbackVx = 0;
    this.knockbackVy = 0;
  }
}
